import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import FranceMonitoringMap from '../components/monitoring/FranceMonitoringMap';
import MonitoringLocationBreakdown from '../components/monitoring/MonitoringLocationBreakdown';
import MonitoringStatsPanel, { formatEuro } from '../components/monitoring/MonitoringStatsPanel';
import MonitoringDeliveryUsagePanel from '../components/monitoring/MonitoringDeliveryUsage';
import MonitoringActivityFeed from '../components/monitoring/MonitoringActivityFeed';
import MonitoringAnalyticsRealtimePanel from '../components/monitoring/MonitoringAnalyticsRealtimePanel';
import type {
  MonitoringActivityEvent,
  MonitoringLocationRegionBreakdown,
  MonitoringLocationSelection,
  MonitoringMapFocus,
  MonitoringPeriod,
  MonitoringSnapshot,
} from '../types/monitoring';
import type { LocationProvider } from '../types/provider';
import {
  resolveMapFocusFromCity,
  resolveMapFocusFromEvent,
  resolveMapFocusFromRegion,
  resolveMapPointIdFromEvent,
  ga4AnalyticsToMapPoints,
} from '../utils/monitoringPresentation';

/** Snapshot DB (carte, stats, activité) — requête lourde côté API */
const POLL_INTERVAL_MS = 60_000;
/** GA4 temps réel — cache API 90 s côté Symfony ; poll aligné */
const ANALYTICS_POLL_INTERVAL_MS = 180_000;
const DEFAULT_LOST_COLOR = '#EBC41D';
const DEFAULT_FOUND_COLOR = '#A12AEB';

const PERIOD_OPTIONS: { value: MonitoringPeriod; label: string }[] = [
  { value: 'day', label: 'Jour' },
  { value: 'week', label: 'Semaine' },
  { value: 'month', label: 'Mois' },
  { value: 'year', label: 'Année' },
];

const MonitoringPage: React.FC = () => {
  const [selectedProvider, setSelectedProvider] = useState<LocationProvider | null>(null);
  const [period, setPeriod] = useState<MonitoringPeriod>('day');
  const [selectedEventKey, setSelectedEventKey] = useState<string | null>(null);
  const [highlightedMapPointId, setHighlightedMapPointId] = useState<string | null>(null);
  const [mapFocus, setMapFocus] = useState<MonitoringMapFocus | null>(null);
  const [mapFocusToken, setMapFocusToken] = useState(0);
  const [locationSelection, setLocationSelection] = useState<MonitoringLocationSelection | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const providerId = selectedProvider?.id ?? undefined;

  useEffect(() => {
    if (!isFullscreen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isFullscreen]);

  const { data: providersData, isLoading: providersLoading } = useQuery({
    queryKey: ['monitoring-providers'],
    queryFn: () => apiService.getProviders({ status: 'active', limit: 1000 }),
    staleTime: 5 * 60 * 1000,
  });

  const providers = providersData?.providers ?? [];

  const { data, isLoading, isError, error, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['admin-monitoring-snapshot', providerId ?? 'all', period],
    queryFn: () =>
      apiService.getMonitoringSnapshot({
        provider_id: providerId,
        period,
      }),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: POLL_INTERVAL_MS,
  });

  const {
    data: analyticsRealtime,
    isLoading: analyticsLoading,
    isFetching: analyticsFetching,
    dataUpdatedAt: analyticsUpdatedAt,
  } = useQuery({
    queryKey: ['admin-monitoring-analytics-realtime'],
    queryFn: () => apiService.getMonitoringAnalyticsRealtime(),
    refetchInterval: ANALYTICS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: ANALYTICS_POLL_INTERVAL_MS,
  });

  const snapshot = data as MonitoringSnapshot | undefined;
  const lostColor = snapshot?.theme?.lost_color || DEFAULT_LOST_COLOR;
  const foundColor = snapshot?.theme?.found_color || DEFAULT_FOUND_COLOR;
  const periodLabel = snapshot?.period_label ?? 'Aujourd’hui';

  const mapPoints = useMemo(() => {
    const sherlookPoints = snapshot?.map_points ?? [];
    const ga4Points = ga4AnalyticsToMapPoints(analyticsRealtime);
    return [...sherlookPoints, ...ga4Points];
  }, [snapshot?.map_points, analyticsRealtime]);

  const statItems = useMemo(() => {
    const stats = snapshot?.stats;
    if (!stats) return [];

    return [
      {
        key: 'lost',
        label: 'Annonces perdues',
        value: stats.announcements_lost,
        sublabel: periodLabel,
        color: lostColor,
      },
      {
        key: 'found',
        label: 'Annonces trouvées',
        value: stats.announcements_found,
        sublabel: periodLabel,
        color: foundColor,
      },
      {
        key: 'matchings',
        label: 'Matchings',
        value: stats.matchings_total,
        sublabel: `${stats.matchings_pending} en attente · ${periodLabel}`,
        color: '#0ea5e9',
      },
      {
        key: 'conversations',
        label: 'Conversations',
        value: stats.conversations_total,
        sublabel: periodLabel,
        color: '#8b5cf6',
      },
      {
        key: 'messages',
        label: 'Messages',
        value: stats.messages_total,
        sublabel: periodLabel,
        color: '#475569',
      },
      {
        key: 'users',
        label: providerId ? 'Utilisateurs (partenaire)' : 'Utilisateurs',
        value: stats.users_total,
        sublabel: periodLabel,
        color: '#334155',
      },
      {
        key: 'stripe-count',
        label: 'Paiements Stripe',
        value: stats.stripe_payments_total,
        sublabel: periodLabel,
        color: '#16a34a',
      },
      {
        key: 'stripe-revenue',
        label: 'Chiffre d’affaires',
        value: formatEuro(stats.stripe_revenue_total_eur),
        sublabel: periodLabel,
        color: '#15803d',
      },
    ];
  }, [snapshot?.stats, lostColor, foundColor, providerId, periodLabel]);

  const lastUpdateLabel = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('fr-FR')
    : '—';

  const analyticsLastUpdateLabel = analyticsUpdatedAt
    ? new Date(analyticsUpdatedAt).toLocaleTimeString('fr-FR')
    : '—';

  const handleActivitySelect = (event: MonitoringActivityEvent, eventKey: string) => {
    setSelectedEventKey(eventKey);
    setHighlightedMapPointId(resolveMapPointIdFromEvent(event));
    setLocationSelection(null);
    const focus = resolveMapFocusFromEvent(event);
    if (focus) {
      setMapFocus(focus);
      setMapFocusToken((value) => value + 1);
    }
  };

  const handleRegionSelect = (region: MonitoringLocationRegionBreakdown) => {
    setLocationSelection({ region: region.region });
    setSelectedEventKey(null);
    setHighlightedMapPointId(null);
    setMapFocus(resolveMapFocusFromRegion(region));
    setMapFocusToken((value) => value + 1);
  };

  const handleCitySelect = (region: MonitoringLocationRegionBreakdown, cityName: string) => {
    const city = region.cities.find((item) => item.city === cityName);
    if (!city) return;
    setLocationSelection({ region: region.region, city: cityName });
    setSelectedEventKey(null);
    setHighlightedMapPointId(null);
    setMapFocus(resolveMapFocusFromCity(city));
    setMapFocusToken((value) => value + 1);
  };

  const handlePeriodChange = (value: MonitoringPeriod) => {
    setPeriod(value);
    setSelectedEventKey(null);
    setHighlightedMapPointId(null);
    setMapFocus(null);
    setMapFocusToken(0);
    setLocationSelection(null);
  };

  const handleProviderChange = (value: LocationProvider | null) => {
    setSelectedProvider(value);
    setSelectedEventKey(null);
    setHighlightedMapPointId(null);
    setMapFocus(null);
    setMapFocusToken(0);
    setLocationSelection(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minHeight: isFullscreen ? 0 : 'calc(100vh - 140px)',
        ...(isFullscreen
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal + 2,
              bgcolor: 'background.default',
              p: { xs: 1.5, md: 2 },
              overflow: 'hidden',
              height: '100vh',
            }
          : {}),
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Monitoring temps réel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Carte France, statistiques live et flux d’activité (rafraîchissement toutes les 60 s, pause si l’onglet est en arrière-plan).
            {snapshot?.provider_filter
              ? ` Filtre : ${snapshot.provider_filter.name}.`
              : ' Vue globale Sherlook.'}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={period}
            onChange={(_, value: MonitoringPeriod | null) => {
              if (value) handlePeriodChange(value);
            }}
            aria-label="Période de monitoring"
          >
            {PERIOD_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Autocomplete
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 280 } }}
            options={providers}
            loading={providersLoading}
            value={selectedProvider}
            onChange={(_, value) => handleProviderChange(value)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Partenaire" placeholder="Tous les partenaires" />
            )}
            clearOnEscape
          />
          <Chip
            size="small"
            color={isFetching ? 'primary' : 'default'}
            label={isFetching ? 'Mise à jour…' : 'Live'}
            icon={isFetching ? <CircularProgress size={12} color="inherit" /> : undefined}
          />
          <Typography variant="caption" color="text.secondary">
            Dernière sync : {lastUpdateLabel}
          </Typography>
          <Tooltip title={isFullscreen ? 'Quitter le plein écran (Échap)' : 'Plein écran'}>
            <IconButton
              size="small"
              onClick={() => setIsFullscreen((value) => !value)}
              aria-label={isFullscreen ? 'Quitter le plein écran' : 'Passer en plein écran'}
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {isError ? (
        <Alert severity="error">
          {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Impossible de charger le monitoring.'}
        </Alert>
      ) : null}

      {isLoading && !snapshot ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.45fr 1fr' },
            gap: 2,
            flex: 1,
            minHeight: 0,
            overflow: isFullscreen ? 'hidden' : 'visible',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              minHeight: 0,
              ...(isFullscreen ? { height: '100%' } : {}),
            }}
          >
            <Box
              sx={{
                height: isFullscreen
                  ? { xs: 280, lg: 'auto' }
                  : { xs: 360, lg: 'calc(100vh - 220px)' },
                flex: isFullscreen ? 1 : '0 0 auto',
                minHeight: isFullscreen ? 280 : { xs: 360, lg: 420 },
                overflow: 'hidden',
              }}
            >
              <FranceMonitoringMap
                points={mapPoints}
                lostColor={lostColor}
                foundColor={foundColor}
                height="100%"
                highlightedPointId={highlightedMapPointId}
                mapFocus={mapFocus}
                mapFocusToken={mapFocusToken}
                layoutRevision={isFullscreen ? 1 : 0}
              />
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <MonitoringLocationBreakdown
                regions={snapshot?.location_breakdown || []}
                selection={locationSelection}
                onRegionSelect={handleRegionSelect}
                onCitySelect={handleCitySelect}
              />
            </Box>
          </Box>

          <Stack spacing={1.5} sx={{ minHeight: 0, overflow: isFullscreen ? 'auto' : 'visible' }}>
            <MonitoringAnalyticsRealtimePanel
              data={analyticsRealtime}
              loading={analyticsLoading}
              isFetching={analyticsFetching}
              lastUpdateLabel={analyticsLastUpdateLabel}
            />
            <MonitoringStatsPanel items={statItems} />
            <MonitoringDeliveryUsagePanel
              usage={snapshot?.delivery_usage}
              periodLabel={periodLabel}
            />
            <MonitoringActivityFeed
              key={`${providerId ?? 'all'}-${period}`}
              events={snapshot?.recent_activity || []}
              periodLabel={periodLabel}
              selectedEventKey={selectedEventKey}
              onEventSelect={handleActivitySelect}
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default MonitoringPage;
